import React from "react";
import { cn } from "@/lib/utils";

/**
 * Card — surface container primitive.
 *
 * Uses a single flat drop shadow (no glow) and a hairline border.
 * Radius max = 14px (per token constraint). NO oversized rounded blob
 * radii (24px+) — the check_ai_look.sh gate enforces this on the
 * design-system folder.
 *
 * Composition:
 *   <Card>
 *     <CardHeader title="Title" description="…" actions={<Button…/>} />
 *     <CardBody>…</CardBody>
 *     <CardFooter>…</CardFooter>
 *   </Card>
 */

export const Card = React.forwardRef(function Card(
  { className, elevated = false, "data-testid": testId, children, ...rest },
  ref
) {
  return (
    <div
      ref={ref}
      data-testid={testId}
      className={cn(
        "rounded-[var(--ds-radius-lg)] border bg-[var(--ds-color-surface)]",
        "border-[var(--ds-color-border)]",
        elevated ? "shadow-[var(--ds-shadow-raised)]" : "shadow-[var(--ds-shadow-card)]",
        className
      )}
      {...rest}
    >
      {children}
    </div>
  );
});
Card.displayName = "DSCard";

export function CardHeader({ title, description, actions, className, children }) {
  return (
    <div
      className={cn(
        "flex items-start justify-between gap-4 px-5 pt-5 pb-3",
        className
      )}
    >
      <div className="min-w-0">
        {title && (
          <div className="text-[16px] font-semibold text-[var(--ds-color-ink)] leading-snug">
            {title}
          </div>
        )}
        {description && (
          <div className="mt-1 text-[13px] text-[var(--ds-color-ink-muted)] leading-snug">
            {description}
          </div>
        )}
        {children}
      </div>
      {actions && <div className="shrink-0">{actions}</div>}
    </div>
  );
}

export function CardBody({ className, children }) {
  return <div className={cn("px-5 pb-5", className)}>{children}</div>;
}

export function CardFooter({ className, children }) {
  return (
    <div
      className={cn(
        "flex items-center justify-end gap-3 border-t px-5 py-3",
        "border-[var(--ds-color-border)]",
        className
      )}
    >
      {children}
    </div>
  );
}

export default Card;
